// ---------------------------------------------------------------------------
// useFormatters — locale-aware Intl helpers bound to the current i18n language.
// Replaces all bare `toLocaleString()` / `toLocaleDateString()` calls in admin-web
// so number and date formatting respects the operator's selected locale.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatNumber,
  formatCurrency,
  formatDate,
  formatRelativeTime,
  formatList,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@noxverify/i18n';

export interface Formatters {
  /** Locale currently in effect. */
  locale: SupportedLocale;
  /** Format a number using the current locale (e.g. thousands grouping). */
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Format a currency amount. `currency` must be an ISO 4217 code (e.g. 'USD'). */
  currency: (value: number, currency: string, options?: Intl.NumberFormatOptions) => string;
  /** Format a Date, ISO string, or epoch ms as a localized date/time. */
  date: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  /** Format a relative time delta (e.g. -3, 'day' -> "3 days ago"). */
  relativeTime: (
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: Intl.RelativeTimeFormatOptions,
  ) => string;
  /** Format an array of strings as a localized list. */
  list: (values: readonly string[], options?: Intl.ListFormatOptions) => string;
}

export function useFormatters(): Formatters {
  const { i18n } = useTranslation();
  const locale = (isSupportedLocale(i18n.language)
    ? i18n.language
    : DEFAULT_LOCALE) as SupportedLocale;

  return useMemo<Formatters>(
    () => ({
      locale,
      number: (value, options) => formatNumber(value, locale, options),
      currency: (value, currency, options) => formatCurrency(value, locale, currency, options),
      date: (value, options) => formatDate(value, locale, options),
      relativeTime: (value, unit, options) => formatRelativeTime(value, unit, locale, options),
      list: (values, options) => formatList(values, locale, options),
    }),
    [locale],
  );
}
